import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { formatDateUS } from './dateUtils';
import { QRCodeSVG } from 'qrcode.react';
import { QRPreviewModal, buildQrUrl } from '../../shared/WizardComponents';
import { FileText, Download, Printer } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

/**
 * Reusable read-only review modal for Division 26 modules.
 * Displays item details in a clean 2-column layout.
 *
 * Props:
 *  - open, onOpenChange
 *  - config: { moduleName, testIdPrefix, fields: [...fieldDef] }
 *  - item: the data object to display
 *  - manufacturers: [] for resolving manufacturer names
 */
export const Division26ReviewModal = ({
  open,
  onOpenChange,
  config,
  item,
  manufacturers = [],
}) => {
  const [qrModal, setQrModal] = useState(false);

  if (!item) return null;

  const resolveValue = (field) => {
    const raw = item[field.key];

    if (field.type === 'manufacturer') {
      const mfr = manufacturers.find(m => m.id === raw);
      return mfr?.name || item.manufacturerName || '-';
    }
    if (field.type === 'date') {
      return formatDateUS(raw);
    }
    if (field.type === 'select') {
      return raw || '-';
    }
    if (field.type === 'rating') {
      const unit = item[field.unitKey] || '';
      const val = item[field.key];
      return val != null ? `${val} ${unit}` : '-';
    }
    if (raw === null || raw === undefined || raw === '') return '-';
    return String(raw);
  };

  const regularFields = config.fields.filter(f => !f.fullWidth);
  const fullWidthFields = config.fields.filter(f => f.fullWidth);

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid={`${config.testIdPrefix}-review-modal`}>
        <DialogHeader>
          <DialogTitle>{config.moduleName} Details</DialogTitle>
          <DialogDescription>
            Review the {config.moduleName.toLowerCase()} information below.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 py-2 relative">
          {item.qrToken && (
            <button
              type="button"
              className="absolute top-2 right-0 cursor-pointer hover:opacity-70 transition-opacity"
              onClick={() => setQrModal(true)}
              data-testid="review-qr-btn"
              title="Click to enlarge QR code"
            >
              <QRCodeSVG value={buildQrUrl(item.qrToken)} size={96} level="L" />
            </button>
          )}
          {regularFields.map((field) => (
            <div key={field.key} data-testid={`review-${field.key}`}>
              <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{field.label}</dt>
              <dd className="text-sm text-slate-900">{resolveValue(field)}</dd>
            </div>
          ))}
        </div>
        {fullWidthFields.map((field) => (
          <div key={field.key} className="pt-2 border-t" data-testid={`review-${field.key}`}>
            <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{field.label}</dt>
            <dd className="text-sm text-slate-900 whitespace-pre-wrap">{resolveValue(field)}</dd>
          </div>
        ))}
        {/* Submittals section */}
        {item.submittals && item.submittals.length > 0 && (
          <div className="pt-2 border-t">
            <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Submittals / Files</dt>
            <div className="space-y-1.5">
              {item.submittals.map((f) => (
                <div key={f.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="truncate">{f.name}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(1)} KB</span>
                    <span className="text-xs bg-slate-200 text-slate-600 rounded px-1.5 py-0.5 w-20 text-center truncate">{f.dataType || '-'}</span>
                    <a
                      href={`${API}${config.apiBase}/${item.projectId}/${item.id}/submittals/${f.id}/download`}
                      className="text-orange-500 hover:text-orange-700"
                      data-testid={`download-submittal-${f.id}`}
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Footer */}
        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid={`${config.testIdPrefix}-review-close-btn`}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    {item.qrToken && (
      <QRPreviewModal
        open={qrModal}
        onClose={() => setQrModal(false)}
        qrToken={item.qrToken}
        productName={item.name}
      />
    )}
    </>
  );
};
